import { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { UseMutateFunction, useQueryClient } from '@tanstack/react-query';
import { useNotifications } from 'app/utils';
import { QueryKeys } from 'data';
import { Modal } from './modal';

export interface IDeleteModalProps {
  entityName: string;
  onClose: () => void;
  onSettled?: () => void;
}

export interface DeleteModalProps extends IDeleteModalProps {
  title: string;
  modalText: string;
  successMessage: string | ReactNode;
  redirectPath: string;
  queryKey: QueryKeys;
  deleteMethod: UseMutateFunction<void, AxiosError<unknown, unknown>, string, unknown>;
  isLoading: boolean;
}

const DeleteModal = ({
  title,
  modalText,
  entityName,
  successMessage,
  redirectPath,
  queryKey,
  deleteMethod,
  onClose,
  onSettled,
  isLoading,
}: DeleteModalProps) => {
  const queryClient = useQueryClient();
  const { showSuccessMessage } = useNotifications();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const onSuccess = async () => {
    if (pathname !== redirectPath) navigate(redirectPath);
    showSuccessMessage(successMessage);
    await queryClient.invalidateQueries({
      queryKey: [queryKey],
      exact: true,
      refetchType: 'active',
    });
  };

  const onSubmit = () => {
    deleteMethod(entityName, {
      onSuccess,
      onSettled,
    });
  };

  return (
    <Modal
      withConfirm
      onSubmit={onSubmit}
      onClose={onClose}
      title={title}
      text={modalText}
      isLoading={isLoading}
      submitButtonProps={{ type: 'button' }}
      submitText="Confirm"
    />
  );
};

export default DeleteModal;
